// overlayKit/TextHighlight.tsx — keyword punches in mid-sentence.
// Migrated from ChocolateFullPreview inline TextHighlight.
import React from "react";
import { useCurrentFrame, useVideoConfig, interpolate } from "remotion";
import { appear, FONT, TS, win } from "./utils";
import { useColor } from "./palette";

export type TextHighlightProps = {
  pre: string;
  /** Highlighted phrase. May contain "\n" to break it onto stacked lines (Yash 2026-07-29 —
   *  long punch lines read better broken than run across the frame). A single-line `hi`
   *  renders exactly as before, so existing productions are unaffected. */
  hi: string;
  post?: string;
  hiS: number;
  outS: number;
  /** Palette token for the highlighted word (default "primary"). */
  hiColor?: string;
  /** Palette token for pre/post dim text (default "cream"). */
  dimColor?: string;
  /** Force `pre` (and `post`) onto their own rows instead of flowing inline with `hi`.
   *  Default false = legacy inline behavior, so Jagannath / MonsterEnergy are untouched.
   *  Set this whenever pre+hi+post would exceed the 1080px box — otherwise the browser
   *  wraps at whatever word happens to cross the edge, which is not a design decision. */
  preOwnLine?: boolean;
};

export const TextHighlight: React.FC<TextHighlightProps> = ({
  pre, hi, post = "", hiS, outS, hiColor = "primary", dimColor = "cream",
  preOwnLine = false,
}) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const t = frame / fps;
  const preS = hiS - 0.3;
  const op = win(t, preS, outS);
  if (op <= 0.001) return null;
  const hiOp = appear(t, hiS, 0.14);
  const hiSc = interpolate(t, [hiS, hiS + 0.25], [0.8, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  const hiHex = useColor(hiColor);
  const dimHex = useColor(dimColor);

  const hiLines = hi.split("\n");
  const hiStyle: React.CSSProperties = {
    color: hiHex, fontSize: 58, fontWeight: 900, letterSpacing: "0.04em",
    textShadow: `0 0 22px ${hiHex}55, ${TS}`, opacity: hiOp,
    display: "inline-block", transform: `scale(${hiSc})`, transformOrigin: "left center",
  };
  const preStyle: React.CSSProperties = { color: dimHex, fontSize: 38, fontWeight: 500, textShadow: TS, opacity: appear(t, preS, 0.2) };
  const postStyle: React.CSSProperties = { color: dimHex, fontSize: 38, fontWeight: 500, textShadow: TS, opacity: appear(t, hiS + 0.25, 0.2) };

  // Stacked: pre / each hi line / post become their own blocks, left-aligned.
  if (hiLines.length > 1 || preOwnLine) {
    return (
      <div style={{ position: "absolute", top: 18, left: 22, fontFamily: FONT, opacity: op }}>
        <div style={{ textAlign: "left", maxWidth: 1080, display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 4 }}>
          {pre && <div style={preStyle}>{pre}</div>}
          {hiLines.map((ln, i) => (
            <div key={i} style={{ ...hiStyle, lineHeight: 1.08 }}>{ln}</div>
          ))}
          {post && <div style={postStyle}>{post}</div>}
        </div>
      </div>
    );
  }

  return (
    <div style={{ position: "absolute", top: 18, left: 22, fontFamily: FONT, opacity: op }}>
      <div style={{ textAlign: "left", maxWidth: 1080 }}>
        {pre && <span style={{ ...preStyle }}>{pre} </span>}
        <span style={hiStyle}>{hi}</span>
        {post && <span style={{ ...postStyle }}> {post}</span>}
      </div>
    </div>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
